import * as THREE from 'three';
import LocalTankController from '../../entities/controllers/LocalTankController';
import InputHandler from '../../InputHandler';
import Multiplayer from '../../Multiplayer';
import ProjectileSystem from '../../ProjectileSystem';
import VisualEffectHelper from '../../VisualEffectHelper';
import GameScene from '../GameScene';
import EnvManager from '../../managers/EnvManager';
import EventBus from '../../EventBus';
import socketService from '../../SocketService';

export default class GameController {
  camera: THREE.Camera;
  collidables: THREE.Object3D[];
  selfTank: LocalTankController | null;
  inputHandler: InputHandler | null;
  scene: THREE.Scene;
  envManager: EnvManager | null;
  username: string | null;
  selectedSkin: string | null;
  visualEffectHelper: any;
  projectileSystem: ProjectileSystem;
  multiplayer: Multiplayer;
  running?: boolean;
  private onWeaponChangeRef?: (gun: string) => void;

  constructor(
    camera: THREE.Camera,
    gameScene: GameScene,
    TWEEN: any,
    envManager: EnvManager | null = null,
    username: string | null = null,
    selectedSkin: string | null = null
  ) {
    if (gameScene.collidables) {
      console.log('GameController Collidables', gameScene.collidables);
    }

    this.camera = camera as THREE.Camera;
    this.collidables = gameScene.collidables as THREE.Object3D[];
    this.selfTank = null;
    this.inputHandler = null;
    this.scene = gameScene.scene as THREE.Scene;
    this.envManager = envManager;
    this.username = username;
    this.selectedSkin = selectedSkin;
    this.visualEffectHelper = new VisualEffectHelper(this.scene, TWEEN);
    this.visualEffectHelper.initExplosionPool(5);
    this.projectileSystem = new ProjectileSystem(this.scene, this.collidables, this.visualEffectHelper);
    this.projectileSystem.initProjectilePool(10);
    this.multiplayer = new Multiplayer(this.scene, this.collidables, this.projectileSystem, this.selfTank);
  }

  initGame() {
    const onSkinChange = (skin: string) => {
      if (this.selfTank && this.selfTank.setSkin) {
        this.selfTank.setSkin(skin);
      }
    };
    const onNameChange = (name: string) => {
      if (this.selfTank && this.selfTank.setName) {
        this.selfTank.setName(name);
      }
      if (this.multiplayer && (this.multiplayer as any).updateName) {
        (this.multiplayer as any).updateName(name);
      }
    };
    this.inputHandler = new InputHandler(onSkinChange, onNameChange, this.envManager);
    this.selfTank = new LocalTankController(this.scene, this.visualEffectHelper, this.projectileSystem);
    this.selfTank.mountCamera(this.camera);
    this.selfTank.addToScene();
    // Include self tank in collidables so remote projectiles can hit us
    if (this.selfTank.tankModel) {
      this.collidables.push(this.selfTank.tankModel);
    }

    this.multiplayer.updateSelfTank(this.selfTank);

    if (this.selectedSkin && this.selfTank.setSkin) {
      this.selfTank.setSkin(this.selectedSkin);
    }
    if (this.username && this.selfTank.setName) {
      this.selfTank.setName(this.username);
    }

    for (let collidable in this.collidables) {
      console.log(collidable);
    }
    console.log('Game initialized. Collidables:', this.collidables);

    try {
      this.visualEffectHelper.warmup();
      this.projectileSystem.warmup();
    } catch (e) {
      console.warn('Warmup skipped:', e);
    }

    // Listen for weapon changes from UI
    this.onWeaponChangeRef = (gun: string) => {
      try {
        this.projectileSystem.setWeapon(gun);
      } catch (e) {
        console.warn('Failed to set weapon', gun, e);
      }
    };
    EventBus.on('weapon:change', this.onWeaponChangeRef);
  }

  activate() {
    if (this.running) 
      throw new Error('Game is already running');
    
    this.running = true;
    this.multiplayer.initializeListeners();
    this.multiplayer.requestPlayers();
    this.inputHandler.initInputHandling();
    console.log('Game started.');

    // Ensure server knows about us in 3D session and push initial state
    try {
      if (this.username && this.selectedSkin) {
        socketService.AddPlayer(this.username, this.selectedSkin);
      }
      if (this.selfTank) {
        this.selfTank.sendPlayerUpdate();
      }
    } catch (e) {
      console.warn('Initial 3D presence or state push failed', e);
    }
  }

  deactivate() {
    if (!this.running) return;
    this.multiplayer.removeListeners();
    this.inputHandler.removeInputHandling();
    if (this.onWeaponChangeRef) {
      EventBus.off('weapon:change', this.onWeaponChangeRef);
      this.onWeaponChangeRef = undefined;
    }
    this.running = false;
  }

  update(deltaTime: number) {
    if (!this.running) return;
    this.selfTank.update(deltaTime, this.collidables, this.inputHandler.state);
    this.projectileSystem.update(deltaTime);
    this.visualEffectHelper.update(deltaTime);
  }

  getProjectileSystem() {
    return this.projectileSystem;
  }
}
